import { DefaultApiError, ResponseHandlerOption, type ResponseHandler } from '@microsoft/kiota-abstractions';
import type { FileStreamResult } from '@/api/client/models/index.ts';

function getResponseHeaders(response: Response): Record<string, string[]> {
  const headers: Record<string, string[]> = {};
  response.headers.forEach((value, key) => {
    headers[key] = value.split(',');
  });
  return headers;
}

function getDownloadFileName(contentDisposition: string | null): string | undefined {
  if (!contentDisposition) {
    return undefined;
  }

  const encodedMatch = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (encodedMatch?.[1]) {
    return decodeURIComponent(encodedMatch[1].trim().replace(/^"|"$/g, ''));
  }

  const match = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return match?.[1]?.trim();
}

export function downloadFile(fileResult: FileStreamResult | undefined, fallbackFileName: string): FileStreamResult | undefined {
  const fileStream = fileResult?.fileStream;

  if (!fileStream) {
    return undefined;
  }

  const blob = new Blob([fileStream], {
    type: fileResult.contentType ?? 'application/octet-stream',
  });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileResult.fileDownloadName ?? fallbackFileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return fileResult;
}

export function downloadArrayBufferFile(
  data: ArrayBuffer | undefined,
  fallbackFileName: string,
  contentType: string = 'text/csv;charset=utf-8'
): ArrayBuffer | undefined {
  if (!data) {
    return undefined;
  }

  const blob = new Blob([data], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fallbackFileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return data;
}

export const fileDownloadResponseHandler: ResponseHandler = {
  async handleResponse<NativeResponseType, ModelType>(response: NativeResponseType): Promise<ModelType | undefined> {
    const rawResponse = response as Response;

    if (!rawResponse.ok) {
      const error = new DefaultApiError(rawResponse.statusText || `Request failed with status ${rawResponse.status}`);
      error.responseStatusCode = rawResponse.status;
      error.responseHeaders = getResponseHeaders(rawResponse);
      throw error;
    }

    if (!rawResponse.body) {
      return undefined;
    }

    return {
      contentType: rawResponse.headers.get('content-type'),
      fileDownloadName: getDownloadFileName(rawResponse.headers.get('content-disposition')),
      fileStream: await rawResponse.arrayBuffer(),
    } as ModelType;
  },
};

export function createFileDownloadResponseHandlerOption(): ResponseHandlerOption {
  const responseHandlerOption = new ResponseHandlerOption();
  responseHandlerOption.responseHandler = fileDownloadResponseHandler;
  return responseHandlerOption;
}
